import { tap } from "rxjs/operators";
import { Component, AfterViewInit, ChangeDetectorRef } from "@angular/core";
import { Router } from "@angular/router";
import { Observable } from "rxjs";
import { PaymentOption, Price, Conversion, Trade, Offer, Fiat, Cryptocoin, CryptoWallet } from "@models";
import { VoucherOffer } from "@models/voucher_offer";
import { FiatService } from "@main-services/currency/fiat.service";
import { StepSummaryModal } from "../../../main/components/step-summary/step-summary.modal";
import { BpModalCanDismiss } from "@models/interfaces/bp-modal-can-dismiss";
import { ModalHeader } from "@models/modal_step";
import { Helper } from "@helpers/functions";
import { CryptocoinService } from "@main-services/currency";
import { CryptoWalletService } from "@main-services/wallet/crypto_wallet.service";
import { BpModalHasSave } from "@models/interfaces/bp-modal-has-save";
import { TOKEN_BEST_ID } from "@helpers/constants";
import { Best2TermsModalComponent } from "../best-2-terms-modal/components/best-2-terms-modal.component";
import { AccountService } from "../../../account/services/account.service";
import { Tradable } from "@models/interfaces/tradable";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { MasterdataService } from "@core-services/masterdata.service";
import { InfoService } from "@core-services/info.service";
import { TradeSuccessComponent } from "../../../main/components/step-summary/success-screens/trade-success/trade-success.component";
import { ConfirmModalService } from "@core-services/confirm-modal.service";

@Component({
	templateUrl: "./trade-voucher.component.html",
	styleUrls: ["../trade-modal.component.scss"],
	standalone: false
})
export class TradeVoucherComponent
	extends StepSummaryModal
	implements AfterViewInit, BpModalCanDismiss, BpModalHasSave
{
	private price: Price = null;
	protected category: string = "trade";
	protected label: string = "voucher";
	public defaultCryptoSelected: boolean = false;
	public searchInputValue: string = "";
	public data: VoucherOffer = null;

	public modalCanDismiss(): boolean | Observable<boolean> {
		if (this.isThirdStep && !this.saved) {
			return this.confirmModalService.showConfirmModal("close_window?", "unsaved_data_will_be_lost");
		}
		return true;
	}

	public constructor(
		private masterData: MasterdataService,
		private cryptoCoinService: CryptocoinService,
		private fiatService: FiatService,
		private infoService: InfoService,
		private router: Router,
		private apicallService: ApicallService,
		private walletService: CryptoWalletService,
		private accountService: AccountService,
		private ref: ChangeDetectorRef,
		protected confirmModalService: ConfirmModalService
	) {
		super(confirmModalService);
	}

	// -------------------- CUSTOM FILTER FOR INFINITE SCROLL LIST
	public filterTradableList = (tradables: Tradable[]): Tradable[] =>
		tradables.filter((tradable) => tradable.isBuyActive());
	// --------------------

	public hasTermsAccepted(): boolean {
		return this.accepted_terms_data;
	}

	public get code(): string {
		return this.data._code;
	}

	public set code(newCode: string) {
		this.data._code = newCode;
	}

	public get vouchers(): PaymentOption[] {
		return this.masterData.payment_options.filter((p) => p.is_voucher).filter((p) => p.isEnabled());
	}

	public get provider(): PaymentOption {
		return this.masterData.getPaymentOptionById(this.data.getPaymentOptionId());
	}

	public set provider(p: PaymentOption) {
		this.data.setPaymentOption(p);
	}

	public get isFirstStep(): boolean {
		return (this.provider == null && this.isEdit(0)) || this.isEdit(1);
	}

	public get isSecondStep(): boolean {
		return (this.provider != null && !this.wallet && this.isEdit(0)) || this.isEdit(2);
	}

	public get isThirdStep(): boolean {
		return (this.provider != null && !!this.wallet && this.isEdit(0)) || this.isEdit(3);
	}

	public stepBack(): void {
		this.ref.detectChanges();
		if (this.isThirdStep) {
			this.renewWallet();
		} else if (this.provider != null) {
			this.provider = new PaymentOption();
		}
	}

	public get wallet(): CryptoWallet {
		return this.walletService.getItemById(this.data.getWalletId());
	}

	public set wallet(w: CryptoWallet) {
		this.data.setWallet(w);
	}

	public get dirty(): boolean {
		return this.isThirdStep || this.summary;
	}

	public get fiat(): Fiat {
		return this.fiatService.getItemById("1");
	}

	public selectVoucher(v: PaymentOption): void {
		if (this.edit > 0) {
			if (this.provider != null && this.provider.getId() === v.getId()) {
				this.summary = true;
				return;
			}
		}

		this.edit = 0;
		this.provider = v;
		this.doTrapFocusTime();
	}

	// BEST 2.0 opt-in terms
	public selectWallet(wallet: CryptoWallet): void {
		if (wallet.getCurrencyId() === TOKEN_BEST_ID && this.accountService.showBest2TermsOnBestTrade()) {
			this.handleBest2Terms(wallet);
			return;
		}

		this.defaultCryptoSelected = true;
		if (this.edit > 0) {
			if (this.wallet != null && this.wallet.getId() === wallet.getId()) {
				this.summary = true;
				return;
			}
		}
		this.wallet = wallet;
		this.data.setAssetId(wallet.getCurrencyId());

		this.checkPrices().subscribe(
			() => {
				this.edit = 0;
				this.doTrapFocusTime();
			},
			(error) => {
				Helper.handleError(error);
				this.price = null;
				this.renewWallet();
			}
		);
	}

	// TODO: Remove after 03.01.2022
	public handleBest2Terms(wallet: CryptoWallet): void {
		this.modalService.getCurrentModal().hide();
		const best2TermsModal = this.modalService.create(Best2TermsModalComponent, null, { showBackdrop: false });
		best2TermsModal.present();
		best2TermsModal.onDismiss.subscribe((res) => {
			if (res.data === "accepted") {
				this.selectWallet(wallet);
			} else {
				this.tradableId = null;
				this.doTrapFocusTime();
			}
		});
	}

	public get cryptoCoin(): Cryptocoin {
		return this.cryptoCoinService.getItemById(this.wallet.getCurrencyId());
	}

	public getAssetSymbol(): string {
		if (this.wallet) {
			return this.cryptoCoin.getSymbol(false, true);
		}
		return "";
	}

	private checkPrices(): Observable<Price[]> {
		this.accepted_terms_data = false;
		const a = new ApicallOption(Price, this.getApiOption());
		return this.apicallService.post("voucher/prices/default", a).pipe(tap((prices) => (this.price = prices[0])));
	}

	// TODO: eslint fix
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private getApiOption(): any {
		const copy = new VoucherOffer();
		Object.assign(copy, this.data);
		delete copy["wallet_id"];
		return copy;
	}

	public get amounts(): Conversion[] {
		if (this.price == null) {
			return [];
		}
		return this.price.getAmounts();
	}

	public save(): void {
		if (!this.accepted_terms_data) {
			this.data.wiggle("accepted_terms_data");
			this.infoService.setError(this.translationService.translate("info_text_offer_checkboxes_not_checked"));
			return;
		}
		const ret = [];
		const code = this.data.getCode();

		for (let i = 0; i < code.length; i += 4) {
			ret.push(code.substr(i, 4));
		}

		this.data.setCode(ret.join("-"));
		this.apicallService.post("voucher/redeem", new ApicallOption(Trade, this.getApiOption())).subscribe(
			(a) => {
				const trade: Trade = a[0];

				this.wallet.addBalance(trade.getAmountTradable());

				if (this.wallet.isNew()) {
					const realWalletId: string = trade.getWalletId(); // Wallet id received from API
					this.apicallService
						.get("wallet/" + realWalletId, new ApicallOption(CryptoWallet))
						.subscribe((res) => {
							const realWallet = res[0];
							this.walletService.upsert(realWallet);
							if (this.wallet.getCurrencyId() === realWallet.getCurrencyId()) {
								this.walletService.deleteItem(this.wallet);
							}
							this.showSuccess(trade, realWallet);
						});
				} else {
					this.showSuccess(trade, this.wallet);
				}
			},
			(error) => Helper.handleError(error)
		);
	}

	private showSuccess(trade: Trade, wallet: CryptoWallet): void {
		this.done();
		const offer = new Offer();
		offer.setAmountTradable(trade.getAmountTradable());
		offer.setWalletId(wallet.getId());
		offer.setCurrencyId(wallet.getCurrencyId());
		offer.setAmountFiat(trade.getAmountFiat());
		offer.setFiatId(trade.getFiatId());
		offer.setPrice(trade.getPrice());
		this.walletService.transactionReceived();
		this.modalService.create(TradeSuccessComponent, { offer }).present();
	}

	public get accepted_terms_data(): boolean {
		return this.data.accepted_terms_data;
	}

	public set accepted_terms_data(b: boolean) {
		this.data.accepted_terms_data = b;
	}

	private renewWallet(): void {
		this.wallet = new CryptoWallet();
	}

	public get currentStep(): ModalHeader {
		const header: ModalHeader = this.getDefaultHeader(this.getTitle());

		if (
			(this.isSecondStep && this.isEdit(0) && !this.tradableId) ||
			(this.isSecondStep && this.tradableId) ||
			(this.isThirdStep && !this.isEdit(3))
		) {
			header.left = "back";
		} else if (this.isFirstStep && this.isEdit(1)) {
			header.left = "abort";
		}
		if ((this.isFirstStep && this.isEdit(1)) || (this.isSecondStep && this.isEdit(2))) {
			header.right = "ok";
		} else if (
			(this.isFirstStep && !this.isEdit(1)) ||
			(this.isSecondStep && !this.isEdit(2)) ||
			this.isThirdStep
		) {
			header.right = "abort";
		}

		return header;
	}

	public onHeaderButtonClick(name: string): void {
		if (name === "back") {
			if (!this.tradableId) {
				this.stepBack();
			} else {
				this.tradableId = "";
				this.renewWallet();
			}
		} else {
			super.onHeaderButtonClick(name);
		}
	}

	public getTitle(): string {
		if (this.stepNumber === 1 || this.stepNumber === 2) {
			return "redeem_voucher";
		}

		if (this.stepNumber === 3) {
			return "voucher_redeem_asset_header";
		}
		return "";
	}
}
